import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";
import moment from "moment";

import { Police } from "@/types/Police";
import {
  downloadFileFromLink,
  formatAnniversaryDate,
  formatDate,
} from "@/utils";
import policyApi from "@/api/policyApi";
import { PageableReponse, SortDirectionsEnum } from "@/types/ApiResponse";
import { useConfig } from "@/contexts/config";

const ExportPolicesBtn = ({ typePolice }: { typePolice: string[] }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [loadingFile, setIsLoadingFile] = useState(false);
  const { config } = useConfig();

  //@ts-ignore
  if (config?.SHOW_EXPORT_POLICES_BTN !== "true") return <></>;

  const getStatus = (police: Police) => {
    if (police.resilie) {
      if (police.dateResil) {
        return new Date(police.dateResil) > new Date()
          ? t("dashboard.police.resilieFutur") +
              formatDate(police.dateResil, "DD/MM/YYYY")
          : t("dashboard.police.resilieLe") +
              formatDate(police.dateResil, "DD/MM/YYYY");
      }
      return t("dashboard.police.resilie");
    }
    if (police.expiration) {
      return (
        t("dashboard.police.expireLe") +
        formatDate(police.expiration, "DD/MM/YYYY")
      );
    }
    return t("dashboard.police.enCours");
  };

  const getAdressePj = (police: Police) =>
    [
      police.copro !== police.adresse1 ? police.copro : null,
      police.adresse1 ?? "-",
      police.adresse2,
      police.codePostal,
      police.ville,
    ]
      .filter(Boolean)
      .join(", ");

  const getAdresseHorsPj = (police: Police) =>
    [police.adresse1 ?? "-", police.adresse2, police.codePostal, police.ville]
      .filter(Boolean)
      .join(", ");

  const getAdresse = (police: Police) =>
    police.typePolicy.code === "DPJ"
      ? getAdressePj(police)
      : getAdresseHorsPj(police);

  const handleExportPolicesEnCsv = async () => {
    setIsLoadingFile(true);
    try {
      // @ts-ignore
      const result: PageableReponse<Police> =
        await policyApi.getPolicesFiltrees(typePolice, {
          // @ts-ignore
          filtres: {},
          page: 0,
          size: 1000,
          // @ts-ignore
          tri: {
            key: "dateEffet",
            direction: SortDirectionsEnum.DESCENDING,
          },
        });
      const data: Police[] = result.content;

      const dataFiltree = data.map((e) => ({
        "N° immeuble": e.numeroImmeuble,
        ...(typePolice.includes("H") && { "N° bail": e.bail }),
        "N° police": e.policyNumber,
        Risque: getAdresse(e),
        ...(typePolice.includes("DNO") && { Propriétaire: e.proprietaire }),
        "Date d'effet": formatDate(e.startDate, "DD/MM/YYYY"),
        ...(typePolice.includes("DDO")
          ? { RéceptionTravaux: e.receptionTravaux }
          : { Echéance: formatAnniversaryDate(e.contractAnniversaryDate) }),
        Statut: getStatus(e),
      }));

      if (data.length === 0) return;

      const headers = Object.keys(dataFiltree[0]);
      const csvContent = [
        headers.join(";"),
        ...dataFiltree.map((row) =>
          // @ts-ignore
          headers.map((h) => JSON.stringify(row[h] ?? "")).join(";")
        ),
      ].join("\n");

      const BOM = "\uFEFF"; // UTF-8 BOM
      const blob = new Blob([BOM + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);

      const typesPolicesStr = ["DMI&DMP", "DMP&DMI"].includes(
        typePolice.join("&")
      )
        ? "MRI"
        : typePolice.join("&");

      downloadFileFromLink(
        url,
        "polices_" + typesPolicesStr + "_" + moment().format("DD_MM_YYYY"),
        "csv"
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Une erreur est survenue lors de l'export des polices", {
        variant: "error",
      });
    } finally {
      setIsLoadingFile(false);
    }
  };

  return (
    <Button
      variant="contained"
      color={"primary"}
      children={t("common.export")}
      endIcon={
        loadingFile ? (
          <CircularProgress color="inherit" size={16} />
        ) : (
          <GetAppIcon />
        )
      }
      onClick={handleExportPolicesEnCsv}
      disabled={loadingFile}
      style={{ marginLeft: "auto", marginBottom: 15, marginRight: 40 }}
    />
  );
};

export default ExportPolicesBtn;
